import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import type { TemplateSummary } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { formatDate } from '../lib/format';

export function TemplatesPage() {
  const { fetcher } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [name, setName] = useState('');
  const q = useQuery({
    queryKey: ['templates'],
    queryFn: () =>
      fetcher<{ templates: TemplateSummary[] }>('/api/templates'),
  });

  const createM = useMutation({
    mutationFn: async () => {
      const tpl = await fetcher<TemplateSummary>('/api/templates', {
        method: 'POST',
        body: JSON.stringify({
          name,
          body: '<p>Hello {{customer_name}}.</p>',
          variable_schema: [
            {
              name: 'customer_name',
              label: 'Customer name',
              required: true,
            },
          ],
        }),
      });
      return tpl;
    },
    onSuccess: (tpl) => {
      setName('');
      void qc.invalidateQueries({ queryKey: ['templates'] });
      nav(`/templates/${tpl.id}`);
    },
  });

  const deleteM = useMutation({
    mutationFn: (id: string) =>
      fetcher(`/api/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Templates</h1>
      </div>
      <div className="card">
        <h2 className="mb-2 text-sm font-medium">New template</h2>
        <div className="flex gap-2">
          <input
            className="input"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button
            type="button"
            className="btn-primary"
            disabled={!name.trim() || createM.isPending}
            onClick={() => createM.mutate()}
          >
            Create
          </button>
        </div>
        {createM.isError ? (
          <p className="mt-2 text-sm text-red-600">
            {(createM.error as Error).message}
          </p>
        ) : null}
      </div>
      <div className="card overflow-x-auto">
        {q.isLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : q.data && q.data.templates.length > 0 ? (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-2 py-1">Name</th>
                <th className="px-2 py-1">Updated</th>
                <th className="px-2 py-1">Variables</th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {q.data.templates.map((t) => (
                <tr key={t.id} className="border-t border-slate-100">
                  <td className="px-2 py-1">
                    <Link
                      to={`/templates/${t.id}`}
                      className="text-brand hover:underline"
                    >
                      {t.name}
                    </Link>
                  </td>
                  <td className="px-2 py-1 text-slate-600">
                    {formatDate(t.updated_at)}
                  </td>
                  <td className="px-2 py-1 text-slate-600">
                    {(t.variable_schema ?? []).length}
                  </td>
                  <td className="px-2 py-1 text-right">
                    <button
                      type="button"
                      className="btn-danger"
                      onClick={() => {
                        if (confirm(`Delete template "${t.name}"?`)) {
                          deleteM.mutate(t.id);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-slate-500">No templates yet.</p>
        )}
      </div>
    </div>
  );
}
